import { IsNumber, IsString } from "@pdfking/tfunc"

import type { Point } from "../types/geometry/Point"
import type { Rectangle } from "../types/geometry/Rectangle"
import type { SpatialRectangle } from "../types/geometry/SpatialRectangle"
import type { Scale } from "../types/scale/Scale"
import { ScaleEnum } from "../types/scale/ScaleEnum"
import type { Measure, Percentage } from "../types/units/Units"
/**
 * Information needed to compute scale values
 */
type ScaleInfo<T extends Measure> = {
  inner: Rectangle<T>
  outer: SpatialRectangle<T>
  bleed: T
}

/**
 * Helper function to create a Point with the same value for x and y
 */
const createPoint = (value: Percentage): Point<Percentage> => ({
  x: value,
  y: value,
})

/**
 * Calculates sketch scaling based on media and inner dimensions
 */
const sketchScale = <T extends Measure>(
  { inner, outer: media, bleed }: ScaleInfo<T>,
  use_bleed: boolean = false
): Point<Percentage> => {
  const b = use_bleed ? bleed : 0

  return {
    x: (2 * b + media.width) / inner.width,
    y: (2 * b + media.height) / inner.height,
  } as Point<Percentage>
}

/**
 * Scales content to be contained within the target area
 */
const containScale = <T extends Measure>(
  info: ScaleInfo<T>,
  use_bleed: boolean = false
): Point<Percentage> => {
  const { x, y } = sketchScale(info, use_bleed)
  return createPoint(Math.min(x, y) as Percentage)
}

/**
 * Scales content to fill the target area
 */
const fillScale = <T extends Measure>(
  info: ScaleInfo<T>,
  use_bleed: boolean = false
): Point<Percentage> => {
  const { x, y } = sketchScale(info, use_bleed)
  return createPoint(Math.max(x, y) as Percentage)
}

/**
 * Converts a scale value into x and y scaling factors
 */
export const ComputeScale = <T extends Measure>(
  info: ScaleInfo<T>,
  scale: Scale
): Point<Percentage> => {
  // Handle numeric case (same scale for both dimensions)
  //

  if (IsNumber(scale)) {
    return createPoint(scale as Percentage)
  }

  // Handle enum case or string format
  if (IsString(scale)) {
    // Handle enum values directly
    switch (scale) {
      case ScaleEnum.FULL:
        return createPoint(1 as Percentage)

      case ScaleEnum.CONTAIN:
        return containScale(info)

      case ScaleEnum.FILL:
        return fillScale(info)

      case ScaleEnum.SKETCH:
        return sketchScale(info)

      case ScaleEnum.CONTAIN_BLEED:
        return containScale(info, true)

      case ScaleEnum.FILL_BLEED:
        return fillScale(info, true)

      case ScaleEnum.SKETCH_BLEED:
        return sketchScale(info, true)
    }

    if (scale.startsWith("scale")) {
      const s = parseInt(scale.split("-")[1] || "100") / 100
      return createPoint(s as Percentage)
    }

    throw new Error(`Unsupported scale type: ${scale}`)
  }

  return scale || createPoint(1 as Percentage)
}
